precision highp float;

uniform sampler2D bg;
uniform sampler2D trail;
uniform vec2 cursor;
uniform float distortion;
uniform float idleProgress;
uniform float time;
uniform float noiseScale;
uniform float uWinkVal;
uniform float uStrength;
uniform vec2 uEyeCenter;
uniform float uRatio;

varying vec2 vUv;
varying float vWinkInfluence;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

float fbm(vec2 p) {
  float v = 0.0;
  float amp = 0.55;
  for (int i = 0; i < 4; i++) {
    v += amp * noise(p);
    p *= 2.0;
    amp *= 0.5;
  }
  return clamp(v, 0.0, 1.0);
}

float sdRect(vec2 p, vec2 b) {
  vec2 d = abs(p) - b;
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}

float getDigit(vec2 uv, bool isOne) {
  float dist;
  if (isOne) {
    dist = sdRect(uv - vec2(0.5, 0.5), vec2(0.02, 0.35));
  } else {
    float outer = sdRect(uv - vec2(0.5, 0.5), vec2(0.15, 0.35));
    float inner = sdRect(uv - vec2(0.5, 0.5), vec2(0.05, 0.25));
    dist = max(outer, -inner);
  }

  float distPos = max(dist, 0.0);
  float core = smoothstep(0.02, 0.0, dist);
  float glow = exp(-distPos * 15.0) * 0.6;
  float wideGlow = exp(-distPos * 5.0) * 0.2;
  return core + glow + wideGlow;
}

void main() {
  float winkDir = sign(uEyeCenter.y - vUv.y);
  vec2 winkUvOffset = vec2(0.0);
  winkUvOffset.y -= vWinkInfluence * uWinkVal * uStrength * 0.4 * winkDir;
  vec2 modifiedVuv = clamp(vUv + winkUvOffset, 0.0, 1.0);

  vec4 t = texture2D(trail, vUv);

  float power = dot(t.rgb, vec3(0.299, 0.587, 0.114));

  vec2 delta = vUv - cursor + vec2(0.08);
  float deltaLen = max(length(delta), 0.0001);
  vec2 dir = delta / deltaLen;

  vec2 duv = modifiedVuv + dir * power * distortion * 0.1;
  vec4 base = texture2D(bg, duv);

  float gridSize = 55.0;
  vec2 gridUv = vUv;
  gridUv.x *= uRatio;

  vec2 cellUv = fract(gridUv * gridSize);
  vec2 cellId = floor(gridUv * gridSize);

  float cellRand = hash(cellId);
  float shuffleSpeed = 2.0 + cellRand * 5.0;
  float isOneVal = step(0.5, hash(cellId + floor(time * shuffleSpeed + cellRand * 10.0)));

  float binaryShape = getDigit(cellUv, isOneVal > 0.5);

  float pulse = 0.5 + 0.5 * sin(time * (1.5 + cellRand * 3.0));
  binaryShape *= mix(0.7, 1.0, pulse);

  float faceArea = dot(base.rgb, vec3(0.299, 0.587, 0.114));
  faceArea = smoothstep(0.01, 0.1, clamp(faceArea, 0.0, 1.0));

  vec3 digitTint = mix(vec3(0.6, 1.0, 0.8), vec3(1.0), faceArea);
  vec3 binaryFinalColor = base.rgb * digitTint * 1.4 * binaryShape;

  float d = distance(vUv, cursor);
  float innerR = 0.02;
  float outerR = 0.30;
  float smoothMask = smoothstep(outerR, innerR, d);

  float n = fbm(vUv * noiseScale + time * 0.06);
  float noisyMask = mix(smoothMask, smoothMask * (0.45 + 0.55 * n), 0.65);

  float reveal = clamp(power * 2.5 + smoothstep(0.15, 0.02, d), 0.0, 1.0);
  reveal = reveal * (1.0 - clamp(idleProgress, 0.0, 1.0));

  float finalBinaryAlpha = reveal * faceArea;
  vec3 finalBackground = mix(base.rgb, binaryFinalColor, finalBinaryAlpha);

  vec2 chromaDir = normalize(vUv - vec2(0.5) + 0.0001);
  vec2 offsetR = chromaDir * 0.003 * power;
  vec2 offsetB = -chromaDir * 0.003 * power;

  float originalMask = mix(1.0, noisyMask, clamp(idleProgress, 0.0, 1.0));

  vec3 trailCol = vec3(
    texture2D(trail, vUv + offsetR).r,
    texture2D(trail, vUv).g,
    texture2D(trail, vUv + offsetB).b
  );

  vec4 outC = vec4(finalBackground, 1.0) + vec4(trailCol, originalMask * 0.1);
  gl_FragColor = vec4(clamp(outC.rgb, 0.0, 1.0), clamp(outC.a, 0.0, 1.0));
}
